import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Container, 
  Grid, 
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // SVG del dashboard de MekaSys
  const MekaSysSVG = () => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 800 500"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Fondo del dashboard */}
      <rect x="0" y="0" width="800" height="500" rx="10" fill="#f5f7fa" />
      
      {/* Barra de navegación */}
      <rect x="0" y="0" width="800" height="60" rx="10" fill="#1976d2" />
      <circle cx="40" cy="30" r="20" fill="#ffffff" opacity="0.9" />
      <rect x="80" y="20" width="120" height="20" rx="5" fill="#ffffff" opacity="0.7" />
      <rect x="230" y="20" width="80" height="20" rx="5" fill="#ffffff" opacity="0.5" />
      <rect x="340" y="20" width="80" height="20" rx="5" fill="#ffffff" opacity="0.5" />
      <rect x="450" y="20" width="80" height="20" rx="5" fill="#ffffff" opacity="0.5" />
      <circle cx="750" cy="30" r="20" fill="#ffffff" opacity="0.8" />
      
      {/* Panel izquierdo */}
      <rect x="20" y="80" width="250" height="400" rx="10" fill="#ffffff" />
      <rect x="40" y="100" width="210" height="50" rx="5" fill="#e3f2fd" />
      <rect x="50" y="115" width="150" height="20" rx="5" fill="#bbdefb" />
      <rect x="40" y="170" width="210" height="50" rx="5" fill="#e3f2fd" />
      <rect x="50" y="185" width="150" height="20" rx="5" fill="#bbdefb" />
      <rect x="40" y="240" width="210" height="50" rx="5" fill="#e3f2fd" />
      <rect x="50" y="255" width="150" height="20" rx="5" fill="#bbdefb" />
      <rect x="40" y="310" width="210" height="50" rx="5" fill="#e3f2fd" />
      <rect x="50" y="325" width="150" height="20" rx="5" fill="#bbdefb" />
      <rect x="40" y="380" width="210" height="50" rx="5" fill="#e3f2fd" />
      <rect x="50" y="395" width="150" height="20" rx="5" fill="#bbdefb" />
      
      {/* Panel principal */}
      <rect x="290" y="80" width="490" height="190" rx="10" fill="#ffffff" />
      <rect x="310" y="100" width="450" height="40" rx="5" fill="#e8eaf6" />
      <rect x="310" y="160" width="140" height="90" rx="5" fill="#c5cae9" />
      <rect x="470" y="160" width="140" height="90" rx="5" fill="#c5cae9" />
      <rect x="630" y="160" width="130" height="90" rx="5" fill="#c5cae9" />
      
      {/* Gráficos */}
      <rect x="290" y="290" width="490" height="190" rx="10" fill="#ffffff" />
      <rect x="310" y="310" width="450" height="30" rx="5" fill="#e8eaf6" />
      <path d="M320,390 Q370,340 420,380 T520,350 T620,370 T720,330" stroke="#3f51b5" strokeWidth="3" fill="none" />
      <rect x="310" y="420" width="450" height="40" rx="5" fill="#e8eaf6" opacity="0.7" />
    </svg>
  );

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        pt: 12,
        pb: 12,
        overflow: 'hidden',
        position: 'relative',
        background: 'linear-gradient(45deg, rgba(25,118,210,0.05) 0%, rgba(25,118,210,0.1) 100%)'
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid 
            item 
            xs={12} 
            md={6}
            component={motion.div}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              component="h1"
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 2,
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Gestiona tu taller mecánico con MekaSys
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'text.secondary',
                mb: 4,
                fontWeight: 400,
                lineHeight: 1.5
              }}
            >
              Automatiza los procesos de tu taller, mejora la experiencia de tus clientes 
              y aumenta la productividad con nuestra solución integral.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Button
                component={RouterLink}
                to="/contacto"
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  py: 1.5,
                  px: 3,
                  fontWeight: 600
                }}
              >
                Solicitar información
              </Button>
              <Button
                component={RouterLink}
                to="/precios"
                variant="outlined"
                color="primary"
                size="large"
                sx={{
                  py: 1.5,
                  px: 3,
                  fontWeight: 600
                }}
              >
                Ver planes y precios
              </Button>
            </Box>
          </Grid>
          <Grid 
            item 
            xs={12} 
            md={6}
            component={motion.div}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Box
              sx={{
                width: '100%',
                height: 'auto',
                maxWidth: '550px',
                display: 'block',
                margin: '0 auto',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                overflow: 'hidden'
              }}
            >
              <MekaSysSVG />
            </Box>
          </Grid>
        </Grid>
      </Container>
      
      {/* Formas decorativas en el fondo */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 1.5, delay: 0.5 }}
        sx={{
          position: 'absolute',
          top: { xs: '5%', md: '15%' },
          right: { xs: '-15%', md: '5%' },
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(25,118,210,0.1) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0,
        }}
      />
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 1.5, delay: 0.7 }}
        sx={{
          position: 'absolute',
          bottom: { xs: '5%', md: '10%' },
          left: { xs: '-15%', md: '5%' },
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245,0,87,0.1) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0,
        }}
      />
    </Box>
  );
};

export default Hero; 